/* Recipient Data Service
 *
 * Angular module: trackerbase-app.services.recipient-data-service
 * Exported service: RecipientData
 */

import angular from "angular";

import Assert from "tbAssert";
import TbAppConfigDataServiceModule from "../config/tb-app-config/TbAppConfigDataService";

import Recipient from "../models/Recipient";

const mLogSvc = new WeakMap();
const mHttpSvc = new WeakMap();
const mQSvc = new WeakMap();

const mRecipientServerBaseUrl = new WeakMap();

class RecipientDataService {
    constructor($http, $q, $log, TbAppConfigDataService) {
        Assert.StrictAssert($http != null, "Assertion Failure: Missing Http Service Dependency");
        Assert.StrictAssert($q != null, "Assertion Failure: Missing Q Service Dependency");
        Assert.StrictAssert($log != null, "Assertion Failure: Missing Log Service Dependency");
        Assert.StrictAssert(TbAppConfigDataService != null, "Assertion Failure: Missing TrackerBase Application Configuration Data Service Dependency");

        mHttpSvc.set(this, $http);
        mQSvc.set(this, $q);
        mLogSvc.set(this, $log);
        mRecipientServerBaseUrl.set(this, `${TbAppConfigDataService.tbServerBaseUrl}/api/recipients`);
    }

    toString() {
        return "RecipientDataService: (" + mRecipientServerBaseUrl.get(this) + ")";
    }

    _processFilterSpec(filterSpec) {
        if (!angular.isArray(filterSpec)) {
            return null;
        }

        let filterParts = filterSpec.filter((filterSpecPart) => {
                return ((filterSpecPart.filterType != null) &&
                (filterSpecPart.filterContent != null));
            })
            .map((filterSpecPart) => {
                let filterPart = filterSpecPart.filterType + "|" + filterSpecPart.filterContent;
                return filterPart;
            });

        if (filterParts.length > 0) {
            for (let fpIdx = filterParts.length - 2; fpIdx >= 0; --fpIdx) {
                filterParts.unshift("and");
            }

            return filterParts.join("||");
        }
        else {
            return null;
        }
    }

    _processSortSpec(sortSpec) {
        if (sortSpec == null) {
            return null;
        }

        if ((sortSpec.sortTarget == null) || (sortSpec.sortReverse == null)) {
            return null;
        }

        if ((angular.isString(sortSpec.sortTarget)) && (sortSpec.sortTarget.length > 0)) {
            let sortParam = `${sortSpec.sortTarget}|${(sortSpec.sortReverse ? "desc" : "asc")}`;
            return sortParam;
        }
        else {
            return null;
        }

    }

    getAllRecipients(pageNumber, pageSize, filterSpec, sortSpec) {

        let callParams = {};
        if (pageNumber != null) {
            callParams.pagenumber = pageNumber;
        }

        if (pageSize != null) {
            callParams.pagesize = pageSize;
        }

        mLogSvc.get(this).debug("Provdied filter spec");
        mLogSvc.get(this).debug(filterSpec);

        let filterParam = this._processFilterSpec(filterSpec);

        mLogSvc.get(this).debug("Processed Filter Param");
        mLogSvc.get(this).debug(filterParam);

        if (filterParam != null) {
            callParams.q = filterParam;
        }

        mLogSvc.get(this).debug("Provided sort spec");
        mLogSvc.get(this).debug(sortSpec);

        let sortParam = this._processSortSpec(sortSpec);

        mLogSvc.get(this).debug("Processed Sort Param");
        mLogSvc.get(this).debug(sortParam);

        if (sortParam != null) {
            callParams["sort"] = sortParam;
        }

        return mHttpSvc.get(this)({
            url: mRecipientServerBaseUrl.get(this),
            params: callParams,
            method: "GET",
            isArray: false
        })
            .then(
                (serverResponse) => {
                    let processedData = {};

                    processedData.total = serverResponse.data.TotalItems;
                    processedData.currentPage = serverResponse.data.PageNumber;
                    processedData.pageSize = serverResponse.data.PageSize;
                    processedData.recipientData = serverResponse.data.Items.map(function(serverRecipient) {
                        return Recipient.FromServerRecipient(serverRecipient);
                    });

                    return processedData;

                },
                (serverErrorResponse) => {
                    return mQSvc.get(this).reject(serverErrorResponse);
                }
            );

    }

    _getRecipientBase(recipientId, rcptUrlSuffix) {
        let rId = null;
        if (Number.isInteger(recipientId))
        {
            rId = String(recipientId);
        }
        else {
            throw new TypeError("Missing or invalid recipient id.");
        }

        return mHttpSvc.get(this)({
            url: `${mRecipientServerBaseUrl.get(this)}/${rId}${rcptUrlSuffix}`,
            method: "GET",
            isArray: false
        })
        .then(
            (serverResponse) => {
                if (serverResponse == null) {
                    return mQSvc.get(this).reject("No data received from server");
                }
                
                let recipientData = Recipient.FromServerRecipient(serverResponse.data);
                return recipientData;
            }, 
            (serverErrorResponse) => {
                return mQSvc.get(this).reject(serverErrorResponse);
            }
        );
    }

    getRecipientCore(recipientId) {
        return this._getRecipientBase(recipientId, "");
    }
    
    getRecipient(recipientId) {
        return this._getRecipientBase(recipientId, "/details");
    }
    
    updateRecipient(updatedRecipient) {
        let updatedServerRecipient = updatedRecipient.toServerRecipient();
    
        return mHttpSvc.get(this)(
        {
            url: `${mRecipientServerBaseUrl.get(this)}/${updatedRecipient.recipientId}`,
            method: "PATCH",
            isArray: false,
            data: updatedServerRecipient
        })
        .then(
            (serverResponse) => {
                let recipientData = Recipient.FromServerRecipient(serverResponse.data);
                return recipientData;
            },
            (serverErrorResponse) => {
                return mQSvc.get(this).reject(serverErrorResponse);
            }
        );
    }
    
    createRecipient(newRecipient) {
        let newServerRecipient = newRecipient.toServerRecipient();
        delete newServerRecipient["RecipientId"];

        return mHttpSvc.get(this)(
        {
            url: mRecipientServerBaseUrl.get(this),
            method: "POST",
            isArray: false,
            data: newServerRecipient
        })
        .then(
            (serverResponse) => {
                let createdRecipient = Recipient.FromServerRecipient(serverResponse.data);
                return createdRecipient;
            },
            (serverErrorResponse) => {
                return mQSvc.get(this).reject(serverErrorResponse);
            }
        );
    }

    deleteRecipient(recipientId) {
        if (!((Number.isInteger(recipientId)) && (recipientId > 0)))
        {
            mLogSvc.get(this).error("Delete recipient: invalid recipient id.");
            return mQSvc.get(this).reject("Invalid recipient id");
        }

        let rId = recipientId;

        return mHttpSvc.get(this)(
        {
            url: `${mRecipientServerBaseUrl.get(this)}/${rId}`,
            method: "DELETE",
            isArray: false
        })
        .then(
            (serverResponse) => {
                if (serverResponse.status === 204) {
                    return true;
                }
                else {
                    return mQSvc.get(this).reject(`Recieved unexpected status code ${serverResponse.status}`);
                }
            },
            (serverErrorResponse) => {
                return mQSvc.get(this).reject(serverErrorResponse);
            }
        );
    }
}

RecipientDataService.$inject = [ "$http", "$q", "$log", "TbAppConfigDataService" ];

export default angular
    .module("trackerbase-app.services.recipient-data-service", [
        TbAppConfigDataServiceModule
    ])
    .service("RecipientData", RecipientDataService)
    .name;



//////////////////
// WEBPACK FOOTER
// ./src/services/RecipientDataService.js
// module id = 1baN
// module chunks = 1